import { type ChainAvailabilityItem } from '@/models/appointment-chain-availability.ts';
import { type AppointmentChainConfig } from '@/models/appointment-chain-config.ts';

import { useChainAvailability } from '@/hooks/use-chain-availability.ts';

import { dayjs } from '@/utils/dayjsSetup.js';
import { formatInsuranceType } from '@/utils/insurance-utils.ts';

import { CLEARED_CHAIN_PAGING, useBookingStore, useConfigStore, useSearchStore } from '@/store.js';

// `store.js` is untyped JS, so selectors infer `never`; these aliases re-attach the real shapes.
type CurrentCustomer = { customer_id?: { text?: string }[] } | null;
type ChainMainPractitioner = { type: 'specific'; id: string } | { type: 'no_preference' } | null;

const setSearchState = (state: {
    chainAvailability?: ChainAvailabilityItem[];
    chainSuggestionHistory?: ChainAvailabilityItem[][];
}): void => useSearchStore.setState(state as never);

interface UseChainFooterResult {
    availability: ChainAvailabilityItem[];
    isChainSuggestionFetching: boolean;
    hasPreviousSuggestion: boolean;
    runAutoSuggestion: (anchorDate?: string, history?: ChainAvailabilityItem[][]) => Promise<void>;
    handleNextSuggestion: () => Promise<void>;
    handlePreviousSuggestion: () => void;
    handleBack: () => void;
}

/**
 * Drives the chain suggestions screen's auto-suggest + paging.
 *
 * Page and back-stack live in `useSearchStore`, not local state, so they survive the hook unmount
 * triggered by the registration / login flow.
 */
export const useChainFooter = (): UseChainFooterResult => {
    const { fetchAutoSuggestion } = useChainAvailability();
    const isChainSuggestionFetching = useBookingStore((s: any) => s.isChainSuggestionFetching) as boolean;

    const currentCustomer = useConfigStore((state: any) => state.currentCustomer) as CurrentCustomer;
    const selectedChainConfig = useSearchStore(
        (state: any) => state.selectedChainConfig
    ) as AppointmentChainConfig | null;
    const selectedHealthInsurance = useSearchStore((state: any) => state.selectedHealthInsurance) as
        | string
        | null
        | undefined;
    const chainIsReturningPatient = useSearchStore((state: any) => state.chainIsReturningPatient) as boolean | null;
    const chainMainPractitioner = useSearchStore((state: any) => state.chainMainPractitioner) as ChainMainPractitioner;
    const chainAvailability = useSearchStore((state: any) => state.chainAvailability) as ChainAvailabilityItem[];
    const chainSuggestionHistory = useSearchStore(
        (state: any) => state.chainSuggestionHistory
    ) as ChainAvailabilityItem[][];

    const customerId = currentCustomer?.customer_id?.[0]?.text;
    const insuranceType = formatInsuranceType(selectedHealthInsurance);
    const mainPractitionerId = chainMainPractitioner?.type === 'specific' ? chainMainPractitioner.id : undefined;

    const hasPreviousSuggestion = chainSuggestionHistory.length > 0;

    const runAutoSuggestion = async (anchorDate?: string, history: ChainAvailabilityItem[][] = []): Promise<void> => {
        if (!selectedChainConfig || !customerId || !insuranceType || chainIsReturningPatient === null) return;

        const items = await fetchAutoSuggestion({
            customerId: customerId,
            insuranceType: insuranceType,
            isReturning: chainIsReturningPatient,
            instanceId: selectedChainConfig.instance_id,
            chainId: selectedChainConfig.id,
            startDate: anchorDate,
            steps: selectedChainConfig.steps.map((step) => ({ stepOrder: step.order })),
            mainPractitionerId: mainPractitionerId,
        });

        // `undefined` means the fetch failed (toast already shown) — keep the current page.
        if (items === undefined) return;
        setSearchState({ chainAvailability: items, chainSuggestionHistory: history });
    };

    const handleNextSuggestion = async (): Promise<void> => {
        if (useBookingStore.getState().isChainSuggestionFetching) return;
        const firstStepOrder = selectedChainConfig
            ? Math.min(...selectedChainConfig.steps.map((step) => step.order))
            : undefined;
        const firstSlot = chainAvailability.find((item) => item.step_order === firstStepOrder)?.start_date;
        if (!firstSlot) return;

        const anchor = dayjs.utc(firstSlot).add(1, 'day').format('YYYY-MM-DD');
        await runAutoSuggestion(anchor, [...chainSuggestionHistory, chainAvailability]);
    };

    const handlePreviousSuggestion = (): void => {
        if (useBookingStore.getState().isChainSuggestionFetching) return;
        const previous = chainSuggestionHistory.at(-1);
        if (!previous) return;

        setSearchState({
            chainAvailability: previous,
            chainSuggestionHistory: chainSuggestionHistory.slice(0, -1),
        });
    };

    const handleBack = (): void => {
        setSearchState({ ...CLEARED_CHAIN_PAGING });
    };

    return {
        availability: chainAvailability,
        isChainSuggestionFetching: isChainSuggestionFetching,
        hasPreviousSuggestion: hasPreviousSuggestion,
        runAutoSuggestion: runAutoSuggestion,
        handleNextSuggestion: handleNextSuggestion,
        handlePreviousSuggestion: handlePreviousSuggestion,
        handleBack: handleBack,
    };
};
